import { createContext, useContext, useState, useEffect, useCallback, ReactNode } from "react";
import { appName } from "../constants/config";
import { IGlobalSearchFields, IGlobalSearchState } from "../pages/protected/search/global-search.interface";
import AdvanceSearchModal from "../pages/protected/search/advance-search-modal";

interface IAuthContext {
    authInfo: any;
    authLogin: (user: any) => void;
    authLogout: () => void;
    recoveryInfo: any;
    recoveryForgotPassword: (token: string) => void;
    recoveryVerification: (token: string) => void;
    recoveryReset: () => void;
    settingInfo: any;
    settingModeChange: (isDark: boolean) => void;
    pageTitle: string;
    setPageTitle: (title: string) => void;
    globalSearch: IGlobalSearchState;
    setGlobalSearchFields: (fields: IGlobalSearchFields) => void;
    setShowSearchModal: (value: boolean) => void;
    setShowSearchBtn: (value: boolean) => void;
}

const AuthContext = createContext<IAuthContext>({} as IAuthContext);

export const AuthProvider = ({ children }: { children: ReactNode }) => {
    const [recoveryInfo, setRecoveryInfo] = useState<any>(() => {
        let initialValue = localStorage.getItem('recoveryInfo') || {
            forgotToken: null,
            verificationToken: null
        };
        if (typeof initialValue === 'string') {
            initialValue = JSON.parse(initialValue);
        }
        return initialValue;
    });

    const [authInfo, setAuthInfo] = useState<any>(() => {
        let initialValue = localStorage.getItem('authInfo') || {
            authenticated: false,
            user: null,
        };
        if (typeof initialValue === 'string') {
            initialValue = JSON.parse(initialValue);
        }
        return initialValue;
    });

    const [settingInfo, setSettingInfo] = useState<any>(() => {
        let initialValue = localStorage.getItem('settingInfo') || {
            isDarkMode: null,
        };
        if (typeof initialValue === 'string') {
            initialValue = JSON.parse(initialValue);
        }
        return initialValue;
    });

    const [globalSearch, setGlobalSearch] = useState<IGlobalSearchState>({
        showBtn: false,
        isOpen: false,
        fields: {
            inc_bal: true,
            shw_nil: false,
            opn_bal: false,
            to_date: new Date().toISOString().split('T')[0],
        },
    });

    const setGlobalSearchFields = (fields: IGlobalSearchFields) => {
        setGlobalSearch(prev => ({
            ...prev,
            fields,
        }));
    };

    const setShowSearchModal = (value: boolean) => {
        setGlobalSearch(prev => ({
            ...prev,
            isOpen: value,
        }));
    };

    const setShowSearchBtn = (value: boolean) => {
        setGlobalSearch(prev => ({
            ...prev,
            showBtn: value,
        }));
    };

    const toggleDarkMode = useCallback((isDarkMode: boolean) => {
        document.body.setAttribute("data-bs-theme", isDarkMode ? 'dark' : 'light');
    }, []);

    const settingModeChange = useCallback((isDarkMode: boolean) => {
        setSettingInfo({
            ...settingInfo,
            isDarkMode,
        });
        toggleDarkMode(isDarkMode);
    }, [settingInfo, toggleDarkMode]);

    const initializeDarkPalette = useCallback((isDark: boolean) => {
        if (settingInfo.isDarkMode === null) {
            settingModeChange(isDark);
        } else {
            toggleDarkMode(settingInfo.isDarkMode);
        }
    }, [settingInfo.isDarkMode, settingModeChange, toggleDarkMode]);

    useEffect(() => {
        localStorage.setItem('authInfo', JSON.stringify(authInfo));
        localStorage.setItem('recoveryInfo', JSON.stringify(recoveryInfo));
        localStorage.setItem('settingInfo', JSON.stringify(settingInfo));
        const prefersDark = window.matchMedia('(prefers-color-scheme: dark)');
        initializeDarkPalette(prefersDark.matches);
    }, [authInfo, recoveryInfo, settingInfo, initializeDarkPalette]);

    const recoveryForgotPassword = (token: string) => {
        setRecoveryInfo({
            ...recoveryInfo,
            forgotToken: token
        });
    };

    const recoveryVerification = (token: string) => {
        setRecoveryInfo({
            ...recoveryInfo,
            verificationToken: token
        });
    };

    const recoveryReset = () => {
        setAuthInfo({
            ...authInfo,
            forgotToken: null,
            verificationToken: null
        });
    };


    const authLogin = (user: any) => {
        setAuthInfo({
            ...authInfo,
            authenticated: true,
            user,
        });
    };

    const authLogout = () => {
        setAuthInfo({
            ...authInfo,
            authenticated: false,
            user: null,
        });
    };

    const [pageTitle, setPageTitle] = useState<string>(appName);

    let state: IAuthContext = {
        authInfo, authLogin, authLogout,
        recoveryInfo, recoveryForgotPassword, recoveryVerification, recoveryReset,
        settingInfo, settingModeChange,
        pageTitle, setPageTitle,
        globalSearch, setGlobalSearchFields, setShowSearchModal, setShowSearchBtn,
    };

    return <AuthContext.Provider value={state}>
        {children}
        <AdvanceSearchModal />
    </AuthContext.Provider>
};
export const useAuth = () => useContext(AuthContext);